// ── Hero headline font styles (selection-level) ────────────────────────────
// The owner styles PARTS of the hero headline (a word, a phrase, or the whole
// title via select-all) from the hero slide editor's Title toolbar. The
// styling is stored inside the title's limited-HTML rich text as
// `<font face="<family>">…</font>` — the exact shape execCommand("fontName")
// emits — and shared/richText.ts allowlists ONLY the families below.
//
// All families here are ALREADY loaded by client/index.html's single Google
// Fonts stylesheet — adding a new option means adding its family (with a 700
// weight) to BOTH gfonts <link> lines in client/index.html first, plus a
// matching render rule in vault-template.css AND VAULT_CRITICAL_CSS
// (server/publicHead.ts) — see the lockstep comments there.
//
// The default hero design renders in UPPERCASE Oswald (per-element Tailwind
// utilities). The softer faces read best in normal case, so each option
// carries `normalCase` — the render rules give those faces
// `text-transform: none` inside display headings.

export interface HeadlineFontOption {
  /** Exact font-family name stored in the face="" attribute. */
  family: string;
  /** Owner-facing label in the editor's font dropdown. */
  label: string;
  /** Full CSS font-family stack used when rendering. */
  stack: string;
  /** Render in normal case (instead of the design's uppercase). */
  normalCase: boolean;
}

export const HEADLINE_FONT_OPTIONS: readonly HeadlineFontOption[] = [
  {
    // The template's standard display face. Picking it RESTORES the default
    // look (the emitted <font face="Oswald"> renders identically to
    // unwrapped text — same stack, keeps the design's uppercase).
    family: "Oswald",
    label: "Standard (Default)",
    stack: "'Oswald', 'Inter', sans-serif",
    normalCase: false,
  },
  {
    family: "Playfair Display",
    label: "Elegant",
    stack: "'Playfair Display', 'Georgia', serif",
    normalCase: true,
  },
  {
    family: "Fraunces",
    label: "Warm & Soft",
    stack: "'Fraunces', 'Georgia', serif",
    normalCase: true,
  },
  {
    family: "Newsreader",
    label: "Classic Serif",
    stack: "'Newsreader', 'Georgia', serif",
    normalCase: true,
  },
  {
    family: "Dancing Script",
    label: "Script",
    stack: "'Dancing Script', cursive",
    normalCase: true,
  },
] as const;

/** Exact allowlisted family names (the only values valid in face=""). */
export const HEADLINE_FONT_FAMILIES: readonly string[] = HEADLINE_FONT_OPTIONS.map(
  (o) => o.family,
);

/** Case-insensitive lookup: face attribute value → option (or null). */
export function findHeadlineFontByFamily(face: unknown): HeadlineFontOption | null {
  if (typeof face !== "string") return null;
  const f = face.trim().toLowerCase();
  return HEADLINE_FONT_OPTIONS.find((o) => o.family.toLowerCase() === f) ?? null;
}
